import { useCallback } from 'react'
import { useAngularServices } from '@/react/components'
import { InternalAssignee } from '@/react/types/observations'

interface AssignmentData {
  user: number
  answer: number
  deleted: boolean
  fieldmeta: string
}

interface AssignmentResult {
  id?: number
  user?: number
  answer?: number
  deleted?: boolean
  fieldmeta?: string
}

interface AssignmentApiResponse {
  data: Array<AssignmentResult | Record<string, string[]>>
}

interface UseInternalAssigneeAPIProps {
  answerId: number
  observationId?: number
  questionId?: number
  existingAssignees?: InternalAssignee[]
}

export const useInternalAssigneeAPI = ({
  answerId,
  observationId,
  questionId,
  existingAssignees = [],
}: UseInternalAssigneeAPIProps) => {
  const { Api } = useAngularServices()

  const reactivateExistingAssignees = useCallback(
    async (userIds: number[]): Promise<void> => {
      const reactivatePromises: Promise<unknown>[] = []

      userIds.forEach((userId) => {
        const existingAssignee = existingAssignees.find(
          (existing) => existing.user === userId,
        )

        if (existingAssignee) {
          reactivatePromises.push(
            Api.patch(`answers_internal_assignees/${existingAssignee.id}`, {
              deleted: false,
            }),
          )
        }
      })

      if (reactivatePromises.length > 0) {
        await Promise.all(reactivatePromises)
      }
    },
    [Api, existingAssignees],
  )

  const createNewAssignments = useCallback(
    async (
      assignmentData: AssignmentData[],
    ): Promise<{
      success: boolean
      errors: string[]
    }> => {
      if (assignmentData.length === 0) {
        return { success: true, errors: [] }
      }

      const response = await Api.post(
        'answers_internal_assignees',
        assignmentData,
      )

      // Type guard to safely access the response
      let bulkAssignmentResult: AssignmentApiResponse = { data: [] }
      if (
        response &&
        typeof response === 'object' &&
        'data' in response &&
        Array.isArray(response.data)
      ) {
        bulkAssignmentResult = {
          data: response.data as AssignmentApiResponse['data'],
        }
      }

      // Process any errors
      const errorMessages: string[] = []
      const userIds = assignmentData.map((assignment) => assignment.user)

      bulkAssignmentResult.data.forEach((result, index: number) => {
        if (!result || !('id' in result)) {
          // This is an error response
          if (result && typeof result === 'object') {
            Object.entries(result).forEach(([field, errors]) => {
              if (Array.isArray(errors)) {
                errors.forEach((error: string) => {
                  errorMessages.push(`User ${userIds[index]}: ${error}`)
                })
              }
            })
          }
        }
      })

      return {
        success: bulkAssignmentResult.data.length > 0,
        errors: errorMessages,
      }
    },
    [Api],
  )

  const updateAnswerSynchronization = useCallback(async (): Promise<void> => {
    await Api.post(`answers/${answerId}/synchronised`, {
      synchronised: true,
    })
  }, [Api, answerId])

  const updateAnswerStatus = useCallback(async (): Promise<void> => {
    if (observationId && questionId) {
      await Api.patch(`answers/${answerId}`, {
        answer: 'pr',
        observation: observationId,
        question: questionId,
      })
    }
  }, [Api, answerId, observationId, questionId])

  const processInternalAssignments = useCallback(
    async (
      selectedUsers: number[],
    ): Promise<{
      success: boolean
      errors: string[]
    }> => {
      if (selectedUsers.length === 0) {
        return { success: false, errors: ['Please select at least one user'] }
      }

      try {
        const existingUserIds = existingAssignees
          .filter((assignee) => !assignee.deleted)
          .map((assignee) => assignee.user)

        const newUserIds = selectedUsers.filter(
          (userId) => !existingUserIds.includes(userId),
        )

        if (newUserIds.length > 0) {
          // Separate users that need reactivation vs new assignments
          const usersToReactivate: number[] = []
          const usersForNewAssignment: number[] = []

          newUserIds.forEach((userId) => {
            const existingAssignee = existingAssignees.find(
              (existing) => existing.user === userId,
            )

            if (existingAssignee) {
              usersToReactivate.push(userId)
            } else {
              usersForNewAssignment.push(userId)
            }
          })

          // Reactivate existing assignments
          if (usersToReactivate.length > 0) {
            await reactivateExistingAssignees(usersToReactivate)
          }

          // Create new assignments
          const newAssignmentData: AssignmentData[] = usersForNewAssignment.map(
            (userId) => ({
              user: userId,
              answer: answerId,
              deleted: false,
              fieldmeta: 'internal',
            }),
          )

          const assignmentResult = await createNewAssignments(newAssignmentData)

          if (assignmentResult.success || usersToReactivate.length > 0) {
            await updateAnswerSynchronization()
            await updateAnswerStatus()
          }

          return {
            success: true,
            errors: assignmentResult.errors,
          }
        }

        return { success: true, errors: [] }
      } catch (error) {
        console.error('Error processing internal assignments:', error)
        return {
          success: false,
          errors: ['An error occurred while processing assignments'],
        }
      }
    },
    [
      answerId,
      existingAssignees,
      reactivateExistingAssignees,
      createNewAssignments,
      updateAnswerSynchronization,
      updateAnswerStatus,
    ],
  )

  return {
    processInternalAssignments,
    reactivateExistingAssignees,
    createNewAssignments,
    updateAnswerSynchronization,
    updateAnswerStatus,
  }
}
